import { useState } from 'react';
import type { JSX } from 'react';
import { Button } from '../../../_components/Button.js';
import { ArrowRightIcon } from '../../../_components/Icons.js';
import { TextField } from '../../../_components/TextField.js';
import type { Palette } from '../../../_theme/palette.js';
import type { SurveyIdentityConfig, SurveyPage } from '../types.js';
import { MediaBlock } from './MediaBlock.js';
import { usePipe } from './PipingProvider.js';

interface WelcomePageProps {
  palette: Palette;
  page: SurveyPage;
  questionCount: number;
  identityConfig?: SurveyIdentityConfig;
  knownEmail?: string;
  initialEmail?: string;
  onStart: (email: string) => void;
}

// Mirrors the main app builder: ~30s per question, ceil to whole minutes,
// floor of 1 so an empty survey still reads "Takes about 1 minute".
const SECONDS_PER_QUESTION = 30;

export function WelcomePage({
  palette,
  page,
  questionCount,
  identityConfig,
  knownEmail,
  initialEmail,
  onStart,
}: WelcomePageProps): JSX.Element {
  const pipe = usePipe();
  const identityPrompt = resolveIdentityPrompt(identityConfig, knownEmail);
  const [email, setEmail] = useState(initialEmail ?? '');
  const [error, setError] = useState<string | null>(null);

  const primaryMedia = page.media?.[0];
  const ctaLabel = pipe(page.buttonText)?.trim() || 'Start';
  const title = pipe(page.title) || 'Welcome';
  const description = pipe(page.description);

  const handleStart = (): void => {
    const resolved = (knownEmail ?? email).trim();
    if (identityPrompt === 'required' && !isEmail(resolved)) {
      setError('Please enter a valid email to continue.');
      return;
    }
    setError(null);
    onStart(resolved);
  };

  return (
    <div style={{ padding: '32px 28px 28px', textAlign: 'center' }}>
      {primaryMedia && (
        <div style={{ marginBottom: 24 }}>
          <MediaBlock media={primaryMedia} radius={12} maxHeight={260} />
        </div>
      )}
      <h2 style={{ margin: '0 0 8px', color: palette.text, fontSize: 26, fontWeight: 700 }}>
        {title}
      </h2>
      {description && (
        <p style={{ margin: '0 0 20px', color: palette.textMuted, fontSize: 15, lineHeight: 1.6 }}>
          {description}
        </p>
      )}
      {page.showTimeEstimate && <TimeEstimate palette={palette} questionCount={questionCount} />}
      {identityPrompt !== 'hidden' && (
        <IdentityField
          palette={palette}
          config={identityConfig}
          email={email}
          required={identityPrompt === 'required'}
          onChange={(value) => {
            setEmail(value);
            if (error) setError(null);
          }}
        />
      )}
      {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
      <div style={{ marginTop: 4, display: 'flex', justifyContent: 'center' }}>
        <Button palette={palette} onClick={handleStart} trailingIcon={<ArrowRightIcon />}>
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}

function TimeEstimate({ palette, questionCount }: { palette: Palette; questionCount: number }): JSX.Element {
  const minutes = questionCount <= 0
    ? 1
    : Math.max(1, Math.ceil((questionCount * SECONDS_PER_QUESTION) / 60));
  return (
    <div style={{ color: palette.textMuted, fontSize: 13, marginBottom: 12 }}>
      Takes about {minutes} {minutes === 1 ? 'minute' : 'minutes'}
    </div>
  );
}

interface IdentityFieldProps {
  palette: Palette;
  config: SurveyIdentityConfig | undefined;
  email: string;
  required: boolean;
  onChange: (value: string) => void;
}

function IdentityField({ palette, config, email, required, onChange }: IdentityFieldProps): JSX.Element {
  const label = config?.emailLabel?.trim() || 'Your email';
  return (
    <div style={{ margin: '0 auto 16px', maxWidth: 340, textAlign: 'left' }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: palette.text, marginBottom: 6 }}>
        {label}
        {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </label>
      {config?.emailHelpText && (
        <p style={{ margin: '0 0 6px', color: palette.textMuted, fontSize: 12 }}>{config.emailHelpText}</p>
      )}
      <TextField palette={palette} type="email" value={email} onChange={onChange} placeholder="you@example.com" />
    </div>
  );
}

type IdentityPrompt = 'hidden' | 'optional' | 'required';

function resolveIdentityPrompt(
  config: SurveyIdentityConfig | undefined,
  knownEmail: string | undefined,
): IdentityPrompt {
  if (knownEmail && isEmail(knownEmail)) return 'hidden';
  switch (config?.mode) {
    case 'emailRequired':
      return 'required';
    case 'emailOptional':
      return 'optional';
    default:
      return 'hidden';
  }
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
